import { Component, Input, ViewChild, AfterViewInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { IllustrationData, CustomerInfo, FeeData } from '../shared/illustrations.interface';
import { CallCenterChipComponent, CallCenterChipConfig } from '../../../shared/components/call-center-chip/call-center-chip.component';
import { SmallIconMenuComponent, SmallIconMenuAction } from '../../../shared/components/small-icon-menu/small-icon-menu.component';
import { IllustrationsService } from '../../../services/illustrations.service';

@Component({
  selector: 'app-illustrations-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatChipsModule,
    MatMenuModule,
    MatButtonModule,
    MatSnackBarModule,
    CallCenterChipComponent,
    SmallIconMenuComponent
  ],
  templateUrl: './illustrations-table.component.html',
  styleUrls: ['./illustrations-table.component.scss']
})
export class IllustrationsTableComponent implements AfterViewInit, OnChanges {
  @Input() dataSource!: MatTableDataSource<IllustrationData>;
  @Input() totalIllustrations: number = 0; // Összes illusztrációk száma
  @Input() currentPage: number = 0;
  @Input() pageSize: number = 10;
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() statusUpdated = new EventEmitter<void>(); // Event to notify parent of status update
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private illustrationsService: IllustrationsService,
    private snackBar: MatSnackBar
  ) {}

  displayedColumns: string[] = [
    'contactDate',
    'sendDate', 
    'kum',
    'companyInfo',
    'customerInfo',
    'ownership',
    'area',
    'mainPersons',
    'fee',
    'notes',
    'contactInfo',
    'status'
  ];

  // Status menu actions - will be dynamically updated based on current status
  statusMenuActions: SmallIconMenuAction[] = [];

  /**
   * Get grouped illustrations for mobile view
   * Groups areas by illustration ID/common data
   */
  get groupedIllustrations(): IllustrationData[] {
    if (!this.dataSource || !this.dataSource.data) {
      return [];
    }

    const illustrations: IllustrationData[] = [];
    const illustrationMap = new Map<string, IllustrationData>();

    this.dataSource.data.forEach(item => {
      // Create a unique key for grouping (using main illustration data)
      const key = `${item.contactDate}_${item.companyName}_${item.taxNumber}`;
      
      if (!illustrationMap.has(key)) {
        // First area for this illustration - create the grouped item
        const groupedItem: IllustrationData = {
          ...item,
          areas: [item], // Start with current area
        };
        illustrationMap.set(key, groupedItem);
        illustrations.push(groupedItem);
      } else {
        // Additional area for existing illustration - add to areas array
        const existingItem = illustrationMap.get(key)!;
        if (!existingItem.areas) {
          existingItem.areas = [];
        }
        existingItem.areas.push(item);
      }
    });

    return illustrations;
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      // Ne adjuk át a paginatort automatikusan, mert mi kezeljük
      this.dataSource.sort = this.sort;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataSource'] && this.dataSource) {
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    }
  }

  /**
   * Pagination esemény kezelése - továbbítjuk a wrapper komponenshez
   */
  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  getUserChip(userName: string): CallCenterChipConfig {
    return CallCenterChipComponent.createUserChip(userName);
  }

  formatCurrency(feeData: any): { amount: string; symbol: string; currency: string } | null {
    if (!feeData) return null;
    
    let amount: number;
    let currency: string;
    
    // Handle both old number format and new FeeData object format
    if (typeof feeData === 'number') {
      amount = feeData;
      currency = 'EUR';
    } else if (feeData && feeData.amount !== undefined && feeData.currency) {
      amount = feeData.amount;
      currency = feeData.currency.toUpperCase();
    } else {
      return null;
    }
    
    // Format as whole number (no decimals)
    const formattedAmount = Math.round(amount).toLocaleString('hu-HU');
    
    // Determine symbol and return structured data
    switch (currency) {
      case 'HUF':
      case 'FT':
        return {
          amount: formattedAmount,
          symbol: 'Ft',
          currency: 'HUF'
        };
      case 'EUR':
        return {
          amount: formattedAmount,
          symbol: '€',
          currency: 'EUR'
        };
      case 'USD':
        return {
          amount: formattedAmount,
          symbol: '$',
          currency: 'USD'
        };
      default:
        return {
          amount: formattedAmount,
          symbol: currency,
          currency: currency
        };
    }
  }

  formatMultipleFees(fees: FeeData[]): string {
    if (!fees || fees.length === 0) return '';
    
    return fees.map(fee => {
      const formatted = this.formatCurrency(fee);
      return formatted ? `${formatted.amount} ${formatted.symbol}` : '';
    }).filter(fee => fee !== '').join(', ');
  }



  /**
   * Calculate age from birthdate string
   */
  calculateAge(birthDateString: string): number {
    if (!birthDateString) return 0;
    
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  /**
   * Get total count of persons (customers + other entities)
   */
  getTotalPersonsCount(element: IllustrationData): number {
    // Mindig számoljuk össze a customers és otherEntities elemeit
    const customersCount = element.customers?.length || 0;
    const otherEntitiesCount = element.otherEntities?.length || 0;
    
    return customersCount + otherEntitiesCount;
  }

  /**
   * Get count of persons for a specific area (customers + other entities in that area)
   */
  getAreaPersonsCount(area: any): number {
    const customersCount = area.customers?.length || 0;
    const otherEntitiesCount = area.other_entities?.length || 0;
    
    return customersCount + otherEntitiesCount;
  }

  /**
   * Show success snackbar message
   */
  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error snackbar message
   */
  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Get menu actions based on current status
   */
  getStatusMenuActions(currentStatus: string): SmallIconMenuAction[] {
    const allActions: SmallIconMenuAction[] = [
      { icon: 'check', action: 'mark_sent', label: 'Kiküldve jelölés' },
      { icon: 'error', action: 'mark_error', label: 'Hiba jelölés' },
      { icon: 'schedule', action: 'mark_waiting', label: 'Kiküldésre vár jelölés' }
    ];

    // Szűrjük ki az aktuális státusznak megfelelő akciót
    return allActions.filter(action => {
      if (currentStatus === 'Kiküldve' && action.action === 'mark_sent') return false;
      if (currentStatus === 'Hiba' && action.action === 'mark_error') return false;
      if (currentStatus === 'Kiküldésre vár' && action.action === 'mark_waiting') return false;
      return true;
    });
  }

  /**
   * Handle status icon click
   */
  onStatusIconClick(element: IllustrationData, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Frissítjük a menü opciókat a jelenlegi státusz alapján
    this.statusMenuActions = this.getStatusMenuActions(element.status);
    
    console.log('Status icon clicked for illustration:', element);
  }

  /**
   * Handle status menu action clicks
   */
  onStatusActionClicked(action: SmallIconMenuAction, element: IllustrationData): void {
    console.log('Status action clicked:', action, 'for element:', element);
    
    switch (action.action) {
      case 'mark_sent':
        this.markAsSent(element);
        break;
      case 'mark_error':
        this.markAsError(element);
        break;
      case 'mark_waiting':
        this.markAsWaiting(element);
        break;
      default:
        console.log('Unknown action:', action.action);
    }
  }

  /**
   * Mark illustration as sent
   */
  private markAsSent(element: IllustrationData): void {
    console.log('Marking illustration as sent:', element.id);
    
    this.illustrationsService.updateIllustrationStatus(element.id, 'Kiküldve').subscribe({
      next: (response) => {
        console.log('✅ Status updated successfully:', response);
        
        // Update the local data
        element.status = 'Kiküldve';
        
        // Show success message
        this.showSuccessMessage('Az illusztráció sikeresen "Kiküldve" státuszra állítva!');
        
        // Notify parent component to reload data or handle the update
        this.statusUpdated.emit();
      },
      error: (error) => {
        console.error('❌ Error updating status:', error);
        this.showErrorMessage('Hiba történt a státusz frissítésekor: ' + (error.message || 'Ismeretlen hiba'));
      }
    });
  }

  /**
   * Mark illustration as error
   */
  private markAsError(element: IllustrationData): void {
    console.log('Marking illustration as error:', element.id);
    
    this.illustrationsService.updateIllustrationStatus(element.id, 'Hiba').subscribe({
      next: (response) => {
        console.log('✅ Status updated successfully:', response);
        
        // Update the local data
        element.status = 'Hiba';
        
        // Show success message
        this.showSuccessMessage('Az illusztráció sikeresen "Hiba" státuszra állítva!');
        
        // Notify parent component to reload data or handle the update
        this.statusUpdated.emit();
      },
      error: (error) => {
        console.error('❌ Error updating status:', error);
        this.showErrorMessage('Hiba történt a státusz frissítésekor: ' + (error.message || 'Ismeretlen hiba'));
      }
    });
  }

  /**
   * Mark illustration as waiting for sending
   */
  private markAsWaiting(element: IllustrationData): void {
    console.log('Marking illustration as waiting for sending:', element.id);
    
    this.illustrationsService.updateIllustrationStatus(element.id, 'Kiküldésre vár').subscribe({
      next: (response) => {
        console.log('✅ Status updated successfully:', response);
        
        // Update the local data
        element.status = 'Kiküldésre vár';
        
        // Show success message
        this.showSuccessMessage('Az illusztráció sikeresen "Kiküldésre vár" státuszra állítva!');
        
        // Notify parent component to reload data or handle the update
        this.statusUpdated.emit();
      },
      error: (error) => {
        console.error('❌ Error updating status:', error);
        this.showErrorMessage('Hiba történt a státusz frissítésekor: ' + (error.message || 'Ismeretlen hiba'));
      }
    });
  }

  /**
   * Track by function for mobile card view
   */
  trackByElement(index: number, element: IllustrationData): any {
    return element.id || index;
  }

  /**
   * Track by function for areas in mobile view
   */
  trackByAreaData(index: number, areaData: IllustrationData): any {
    return `${areaData.area}_${index}`;
  }
}